import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { DrawerPreview as BaseDrawer } from '@base-ui/react/drawer';
import { Add } from 'iconsax-reactjs';
import type { FC, ReactNode } from 'react';
import { useDirection } from '../i18n';
import { Button } from './Button';
import { cn } from './react';

export type DialogProps = {
  open?: boolean;
  onClose?: () => void;
  children?: ReactNode;
  position: 'center' | 'start' | 'end' | 'bottom' | 'top' | 'full';
  className?: string;
  header?: ReactNode;
  footer?: ReactNode;
  closeButton?: boolean;
  headerClassName?: string;
  footerClassName?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
};

export const Dialog: FC<DialogProps> = ({
  open,
  onClose,
  children,
  position,
  className,
  header,
  footer,
  closeButton = true,
  headerClassName,
  footerClassName,
  size,
  ...rest
}) => {
  const sizeClass = {
    sm: 'w-sm',
    md: 'w-md',
    lg: 'w-lg',
    xl: 'w-xl',
    '2xl': 'w-2xl',
    '3xl': 'w-3xl',
  };

  const dialogDefaultSize = size
    ? sizeClass[size]
    : position === 'end'
      ? 'w-sm'
      : 'w-lg';

  const isDrawer =
    position === 'start' ||
    position === 'end' ||
    position === 'bottom' ||
    position === 'top';
  const BaseComponent = isDrawer ? BaseDrawer : BaseDialog;

  const direction = useDirection();
  const swipeDirection =
    position === 'start'
      ? direction === 'rtl'
        ? 'right'
        : 'left'
      : position === 'end'
        ? direction === 'rtl'
          ? 'left'
          : 'right'
        : position === 'bottom'
          ? 'down'
          : position === 'top'
            ? 'up'
            : undefined;

  const drawerHandle = (
    <div
      className={cn(
        'sticky size-24 rounded-2xl bg-border-tertiary/75 z-20 shrink-0',
        position === 'top' && 'h-2 bottom-2 left-1/2 -translate-x-1/2 mb-1',
        position === 'bottom' && 'h-2 top-2 left-1/2 -translate-x-1/2 mt-1',
        position === 'start' && 'w-2 top-1/2 end-2 -translate-y-1/2 ms-1',
        position === 'end' && 'w-2 top-1/2 start-2 -translate-y-1/2 me-1',
      )}
    />
  );

  return (
    <BaseComponent.Root
      open={open ?? false}
      onOpenChange={(e: boolean) => {
        if (!e) {
          onClose?.();
        }
      }}
      {...(isDrawer ? { swipeDirection } : {})}
      {...rest}
    >
      <BaseComponent.Portal container={document.getElementById('portal-root')}>
        <BaseComponent.Backdrop />

        <BaseComponent.Viewport
          className={cn(
            'fixed inset-0 flex w-svw h-svh bg-content-disabled/30 backdrop-blur-xs',
            'transition-all duration-700 ease-out',
            'data-[starting-style]:bg-transparent data-[starting-style]:backdrop-blur-none',
            'data-[ending-style]:bg-transparent data-[ending-style]:backdrop-blur-none',
            position === 'center' && 'items-center justify-center p-4',
            position === 'bottom' && 'items-end justify-center p-0',
            position === 'top' && 'items-start justify-center p-0',
            position === 'end' && 'items-center justify-end desktop:p-2',
            position === 'start' && 'items-center justify-start desktop:p-2',
          )}
        >
          {/* The actual dialog panel  */}
          <BaseComponent.Popup
            className={cn(
              'bg-background-primary shadow-xl relative',
              'overflow-auto scrollbar-none',
              'transition-all ease-out',
              isDrawer ? 'duration-400' : 'duration-300',
              position === 'center' && [
                'rounded-2xl max-h-[calc(100%-4rem)] max-w-[calc(100%-4rem)]',
                'data-[starting-style]:opacity-0 data-[starting-style]:scale-90',
                'data-[ending-style]:opacity-0 data-[ending-style]:scale-90',
                dialogDefaultSize,
              ],
              position === 'bottom' && [
                'max-w-lg rounded-t-2xl max-h-[calc(100%-4rem)]',
                'data-[starting-style]:translate-y-full',
                'data-[ending-style]:translate-y-full',
                'pb-[env(safe-area-inset-bottom)]',
                dialogDefaultSize,
              ],
              position === 'top' && [
                'max-w-lg rounded-b-2xl max-h-[calc(100%-4rem)]',
                'data-[starting-style]:-translate-y-full',
                'data-[ending-style]:-translate-y-full',
                dialogDefaultSize,
              ],
              position === 'end' && [
                'w-sm max-w-[calc(100%-4rem)] h-full rounded-s-2xl end-0',
                'data-[starting-style]:translate-x-full',
                'data-[ending-style]:translate-x-full',
                'pb-[env(safe-area-inset-bottom)]',
                dialogDefaultSize,
              ],
              position === 'start' && [
                'w-sm max-w-[calc(100%-4rem)] h-full rounded-l-2xl start-0',
                'data-[starting-style]:-translate-x-full',
                'data-[ending-style]:-translate-x-full',
                'pb-[env(safe-area-inset-bottom)]',
                dialogDefaultSize,
              ],
              position === 'full' && [
                'size-full',
                'data-[starting-style]:translate-y-full',
                'data-[ending-style]:translate-y-full',
                'pb-[env(safe-area-inset-bottom)]',
              ],
            )}
          >
            {(position === 'end' || position === 'bottom') && drawerHandle}

            {((closeButton && !isDrawer) || header) && (
              <div
                className={cn(
                  'flex items-center justify-between gap-2 desktop:px-6 px-5 desktop:pt-6 pt-5 sticky top-0 bg-background-primary z-10',
                  headerClassName,
                )}
              >
                {header}
                {closeButton && !isDrawer && (
                  <Button
                    fab
                    size={8}
                    variant="outline"
                    onClick={onClose}
                    className="shrink-0"
                  >
                    <Add
                      size={24}
                      className="text-content-tertiary rotate-45"
                    />
                  </Button>
                )}
              </div>
            )}
            <div className={cn('desktop:p-6 p-5 h-full', className)}>
              {children}
            </div>

            {footer && (
              <div
                className={cn(
                  'flex items-center justify-end desktop:px-6 px-5 desktop:pb-6 pb-5 sticky bottom-0 bg-background-primary',
                  footerClassName,
                )}
              >
                {footer}
              </div>
            )}

            {(position === 'start' || position === 'top') && drawerHandle}
          </BaseComponent.Popup>
        </BaseComponent.Viewport>
      </BaseComponent.Portal>
    </BaseComponent.Root>
  );
};
